import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, Icon } from "@sagot/ui";
import { useRef, type Ref } from "react";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import type { WorkflowOut } from "../types";
import type { DefaultDialog } from "./default-dialogs";
import { canRestore } from "./defaults-model";

const D = "engage.workflows.defaults";

interface DefaultMenuProps {
  workflow: WorkflowOut;
  canManage: boolean;
  canPublish: boolean;
  /** Duplicating a workflow needs a plan that has workflows of its own: below it the item is locked and opens the upgrade sheet. */
  duplicateLocked: boolean;
  /** Paused by a Service module that is switched off: it starts again by itself when the module is on, so there is nothing to resume. */
  blockedByModule?: boolean;
  triggerRef: Ref<HTMLButtonElement>;
  onResume: () => void;
  onOpenDialog: (dialog: DefaultDialog) => void;
}

/** Open · Pause / Resume · Duplicate · Restore default · Compare with default. A default is never deleted and never reordered, so neither is offered. */
export function DefaultMenu({ workflow, canManage, canPublish, duplicateLocked, blockedByModule = false, triggerRef, onResume, onOpenDialog }: DefaultMenuProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const live = workflow.state === "live";
  const restorable = canRestore(workflow);
  // A dialog chosen from the menu owns the focus; the menu must not hand it back to its trigger a moment later (it would steal it from the dialog's first field).
  const dialogChosen = useRef(false);
  const openDialog = (dialog: DefaultDialog) => {
    dialogChosen.current = true;
    onOpenDialog(dialog);
  };
  const keepDialogFocus = (event: Event) => {
    if (!dialogChosen.current) return;
    dialogChosen.current = false;
    event.preventDefault();
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button ref={triggerRef} variant="ghost" size="sm" iconOnly aria-label={t(`${D}.menu.label`, { name: workflow.name })}>
          <Icon name="more-horizontal" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" onCloseAutoFocus={keepDialogFocus}>
        <DropdownMenuItem asChild>
          <AppLink href={consoleRoutes.workflow(summary.slug, workflow.id)}>
            <Icon name="arrow-up-right" />
            {t(`${D}.menu.open`)}
          </AppLink>
        </DropdownMenuItem>
        {canPublish ? (
          live ? (
            <DropdownMenuItem onSelect={() => { openDialog("pause"); }}>
              <Icon name="pause" />
              {t(`${D}.menu.pause`)}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem disabled={blockedByModule} onSelect={onResume}>
              <Icon name="play" />
              {t(blockedByModule ? `${D}.service.module.resumeBlocked` : `${D}.menu.resume`)}
            </DropdownMenuItem>
          )
        ) : null}
        {canManage ? (
          <DropdownMenuItem onSelect={() => { openDialog(duplicateLocked ? "upgrade" : "duplicate"); }}>
            <Icon name="copy" />
            <span className="flex-1">{t(`${D}.menu.duplicate`)}</span>
            {duplicateLocked ? <Badge><Icon name="lock" className="size-3" />{t("engage.workflows.planNeeded", { plan: t("console.plans.standard") })}</Badge> : null}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        {canPublish ? (
          <DropdownMenuItem disabled={!restorable} onSelect={() => { openDialog("restore"); }}>
            <Icon name="rotate-ccw" />
            {t(restorable ? `${D}.menu.restore` : `${D}.menu.restoreDisabled`)}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem onSelect={() => { openDialog("compare"); }}>
          <Icon name="layers" />
          {t(`${D}.menu.compare`)}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
